import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, getDocFromServer, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import AgreementGate from './AgreementGate';
import { isRouteAllowed } from '../routeRoles';
import { roleDestination } from '../pages/Login';

// KLO-only lead-gen machinery (chief_bot's auto-filed batches + the permit
// research queue) — gated behind a single company setting so another
// tenant's deployment has it off by default rather than needing every
// Tracerfy/GHL-wired route hidden one at a time. Checked for EVERY role,
// including owner — unlike the rest of this file, owner does NOT bypass
// this (isRouteAllowed's own owner-bypass is unrelated; this is a separate,
// deliberately unconditional check below).
const FEATURE_GATED_ROUTES = { '/approvals': 'leadGen', '/research': 'leadGen' };

// BUG FIX (navigation/live-updates follow-up, ask #3): nothing bounded the
// very first wait — onAuthStateChanged firing at all — so a stuck Auth/
// Firestore connection (the second-window persistence bug this same follow-
// up fixes in firebase/config.js, or any other transient failure) left user
// undefined forever and this whole guard rendered blank (see the old
// `return null` below) with no way out short of knowing to hard-refresh.
// 12s is comfortably longer than every other timeout already in this file
// (5s each for permissions/features, which only start once auth resolves)
// so a slow-but-working connection never trips this early.
const AUTH_TIMEOUT_MS = 12000;

function FullScreenLoading() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f1123' }}>
      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>Loading…</div>
    </div>
  );
}

function FullScreenRetry({ message }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f1123', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>{message}</div>
        <button onClick={() => window.location.reload()}
          style={{ padding: '10px 20px', background: '#e91e8c', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    </div>
  );
}

export default function PrivateRoute() {
  const [user, setUser]     = useState(undefined); // undefined = still checking
  const [claims, setClaims] = useState(undefined); // undefined = still checking
  const [authTimedOut, setAuthTimedOut] = useState(false);
  const [claimsError, setClaimsError] = useState(false);
  // S3 amended: grant-only permission toggles, read live off userProfiles/{uid}
  // so a toggle flip takes effect on the user's next page load without a
  // re-login. undefined = still checking (only meaningful once claims resolve).
  const [permissions, setPermissions] = useState(undefined);
  const location = useLocation();
  const gatedFeature = FEATURE_GATED_ROUTES[location.pathname];
  // null = "not needed on this route" (resolved, nothing to wait for);
  // undefined = needed but not yet loaded; object = loaded.
  const [features, setFeatures] = useState(gatedFeature ? undefined : null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async u => {
      setUser(u);
      if (!u) { setClaims(null); return; }
      try {
        const tr = await u.getIdTokenResult();
        setClaims(tr.claims || {});
      } catch {
        // BUG FIX (follow-up #2 — "second window redirects to /dashboard"):
        // this used to resolve to {} on ANY getIdTokenResult failure, under
        // the S4v2 Phase A assumption that a no-claim token is still
        // owner-equivalent. routeRoles.js's Phase C cutover removed that
        // null-is-owner branch (isRouteAllowed only trusts the literal
        // 'owner' claim now) — so a transient failure here (a token fetch
        // racing a second tab's IndexedDB/auth persistence bootstrap, the
        // exact scenario reported) silently fell through to "role: null",
        // which isRouteAllowed denies on every route, redirecting even the
        // OWNER to /dashboard with no error shown. One retry with a forced
        // token refresh clears the transient case; if it genuinely keeps
        // failing, surface FullScreenRetry (below) instead of guessing a
        // role and misrouting.
        try {
          const tr = await u.getIdTokenResult(true);
          setClaims(tr.claims || {});
        } catch {
          setClaimsError(true);
        }
      }
    });
    return unsub;
  }, []);

  // Bounds the ONE wait above with no existing timeout — see AUTH_TIMEOUT_MS.
  // Clears itself the moment auth genuinely resolves (does nothing once
  // `user` is no longer undefined), so a normal, working connection never
  // shows anything but FullScreenLoading however briefly it takes.
  useEffect(() => {
    if (user !== undefined) return undefined;
    const timer = setTimeout(() => setAuthTimedOut(true), AUTH_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [user]);

  useEffect(() => {
    // S4v2 PHASE A — owner (role 'owner', OR a null/missing claim — both
    // owner-equivalent in this transitional phase) NEVER reads, waits on, or
    // requires a userProfiles doc — the permissions layer only ADDS access
    // on top of a named role's defaults, and owner already bypasses the
    // whole layer via isRouteAllowed's `if (role === 'owner' || role ==
    // null) return true`. Crew doesn't have a profile doc either. All
    // resolve to {} synchronously, with zero network dependency, so none can
    // ever hang here regardless of connectivity.
    if (!user || claims === undefined) { setPermissions(undefined); return; }
    const role = claims?.role || null;
    if (role !== 'sales_rep' && role !== 'sales_manager') {
      setPermissions({});
      return;
    }
    // sales_rep/sales_manager DO depend on a live read here — bound it. A
    // missing doc still fires a prompt snapshot (exists()===false) under
    // normal connectivity, but an offline client with no cache can leave
    // onSnapshot never firing at all; without a timeout that stalls this
    // guard's loading check forever and blanks EVERY route the user visits,
    // not just one. 5s is generous for a same-region Firestore read; on
    // timeout, resolve to {} — role defaults still apply, never an infinite
    // blank screen.
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) { settled = true; setPermissions({}); }
    }, 5000);
    const unsub = onSnapshot(
      doc(db, 'userProfiles', user.uid),
      snap => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        setPermissions(snap.exists() ? (snap.data().permissions || {}) : {});
      },
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        setPermissions({}); // read failure — fail closed to no extra grants
      }
    );
    return () => { clearTimeout(timer); unsub(); };
  }, [user, claims]);

  // settings/company.features — ONLY read when the current route is actually
  // feature-gated (most routes never touch this), so this never adds a
  // blocking read to every page load. Deliberately a SERVER-sourced read
  // (getDocFromServer), not onSnapshot — this app runs Firestore with
  // persistentLocalCache (src/firebase/config.js), and onSnapshot's
  // cache-first-then-server emission pattern is wrong for a security gate:
  // an early redirect fired off a stale cached value can never be undone by
  // the later, correct server value arriving (the component has already
  // navigated away and unmounted by then) — caught live while verifying
  // this exact flag flip. Falls back to the (possibly cached) getDoc only
  // on a genuine fetch failure (e.g. offline), and fails CLOSED to {} if
  // even that fails — every feature off, matching this file's existing
  // permissions-read philosophy.
  useEffect(() => {
    if (!gatedFeature) { setFeatures(null); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => { if (!cancelled) { cancelled = true; setFeatures({}); } }, 5000);
    (async () => {
      let data = {};
      try {
        const snap = await getDocFromServer(doc(db, 'settings', 'company'));
        data = snap.exists() ? (snap.data().features || {}) : {};
      } catch (_) {
        try {
          const snap = await getDoc(doc(db, 'settings', 'company'));
          data = snap.exists() ? (snap.data().features || {}) : {};
        } catch (_) {
          data = {}; // fail closed
        }
      }
      if (!cancelled) { cancelled = true; clearTimeout(timer); setFeatures(data); }
    })();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [gatedFeature]);

  // BUG FIX (navigation/live-updates follow-up, ask #3 — "never render a
  // blank screen"): user===undefined with authTimedOut true means
  // onAuthStateChanged never fired within AUTH_TIMEOUT_MS — show an error +
  // Retry instead of staying blank forever. Once auth itself resolves,
  // claims/permissions/features are already individually timeout-bounded
  // above (5s each) and always settle to a safe value, so no further timeout
  // is needed past this point — just a visible loading state instead of
  // nothing while they do.
  if (user === undefined && authTimedOut) {
    return <FullScreenRetry message="Taking longer than expected to sign you in. Check your connection and retry." />;
  }
  if (claimsError) {
    return <FullScreenRetry message="Couldn't verify your account on this tab. Check your connection and retry." />;
  }
  // Resolve auth state, role claims, permission grants, AND the feature flag
  // (when this route needs one) BEFORE rendering anything guarded — no flash
  // of the wrong role's UI while any are in flight.
  if (user === undefined || (user && claims === undefined) || (user && claims && permissions === undefined) || features === undefined) {
    return <FullScreenLoading />;
  }

  if (!user) return <Navigate to="/login" replace />;

  const role = claims.role || null;
  if (!isRouteAllowed(location.pathname, role, permissions)) {
    return <Navigate to={roleDestination(claims)} replace />;
  }

  // Feature-gated routes (Approvals, Research) deny for EVERY role, owner
  // included, when settings/company.features[gatedFeature] isn't exactly
  // true — this is deliberately separate from (and runs after) the role
  // check above, since isRouteAllowed's owner-bypass would otherwise let
  // owner straight through regardless of the flag.
  if (gatedFeature && !(features && features[gatedFeature] === true)) {
    return <Navigate to={roleDestination(claims)} replace />;
  }

  // AgreementGate blocks sales roles until the agreement is signed; it is a
  // zero-read pass-through for owner and crew. Public token routes never
  // mount PrivateRoute, so they are unaffected.
  return <AgreementGate><Outlet /></AgreementGate>;
}
